import { useMutation, useQueryClient } from '@tanstack/react-query';

import { businessUnitQueryKeys } from '@xemplo/gp-constants';
import { BaseBusinessUnit, ResultV2 } from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { BusinessUnitAPI } from './business-unit-query.constants';
import { buildIntegrationSettingsPayload } from './business-unit-query.helpers';
import {
  BusinessUnitIntegrationSetting,
  DeleteBusinessUnitProps,
  UpdateIntegrationSettingsProps,
  UpdateIntegrationSettingsVariables,
} from './business-unit-query.types';

export const useDeleteBusinessUnit = (props: DeleteBusinessUnitProps) => {
  const { onError, onSuccess } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BaseBusinessUnit) => {
      const { id } = payload;
      const url = apiUrlBuilder(
        BusinessUnitAPI.deleteBusinessUnit(id),
        applicationApiUrl,
      );

      const resp = await client.delete<BaseBusinessUnit>(url);
      return resp.data;
    },
    onSuccess: (
      data: BaseBusinessUnit,
      variables: BaseBusinessUnit,
      onMutateResult: unknown,
      context: unknown,
    ) => {
      queryClient.invalidateQueries({
        queryKey: businessUnitQueryKeys.all,
      });
      onSuccess?.(data, variables, onMutateResult, context);
    },
    onError,
  });
};

/**
 * Updates a business unit's integration settings via a full-replace PUT. The request
 * carries the complete set of settings (merged from the current form values and the GET
 * rows so unrendered rows are preserved); the response is the complete post-write set,
 * primed straight into the query cache (pessimistic, no refetch). See docs/adr/0003.
 */
export const useUpdateIntegrationSettings = (props: UpdateIntegrationSettingsProps) => {
  const { businessUnitId } = props;
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const queryClient = useQueryClient();

  const url = apiUrlBuilder(
    BusinessUnitAPI.integrationSettings(businessUnitId),
    applicationApiUrl,
  );

  return useMutation({
    mutationFn: async ({ values, originalRows }: UpdateIntegrationSettingsVariables) => {
      const payload = buildIntegrationSettingsPayload(values, originalRows);
      const resp = await client.put<ResultV2<BusinessUnitIntegrationSetting[]>>(
        url,
        payload,
      );
      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        businessUnitQueryKeys.integrationSettings(String(businessUnitId), url),
        data,
      );
    },
  });
};
